/**
 * BackButton — Smart Back Navigation Component
 * 
 * PHILOSOPHY: This component navigates to where the user CAME FROM, not a
 * hardcoded route. If there's no navigation history (direct URL visit, new tab),
 * it falls back to the provided fallbackPath (the logical parent route).
 * 
 * USAGE:
 * <BackButton fallbackPath="/my-listings" fallbackLabel="Back to My Listings" />
 * 
 * IMPORTANT FOR FUTURE DEVELOPMENT:
 * - Always use this component for "back" navigation instead of hardcoded <Link>
 * - Breadcrumbs (PageBreadcrumb) remain hierarchical for structural orientation
 * - This button is for chronological "where I came from" navigation
 * - The fallbackPath should be the logical parent route in the hierarchy
 */

import { ArrowLeft } from 'lucide-react';
import { useNavigationHistory } from '@/contexts/NavigationHistoryContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useUserPersona } from '@/hooks/useUserPersona';
import { LANDING_FALLBACK } from '@/lib/pageLevels';

interface BackButtonProps {
  /** Route to navigate to when there's no history (e.g., direct URL visit) */
  fallbackPath: string;
  /** Label shown next to the arrow. Defaults to "Back" */
  fallbackLabel?: string;
  /** Render as a ghost icon button (no label) instead of a text link */
  iconOnly?: boolean;
  /** Optional callback before navigating (e.g., unsaved changes check). Return false to prevent navigation. */
  onBeforeNavigate?: () => boolean | void;
  /** Additional CSS classes */
  className?: string;
}

const BackButton = ({
  fallbackPath,
  fallbackLabel = 'Back',
  iconOnly = false,
  onBeforeNavigate,
  className,
}: BackButtonProps) => {
  const { goBack } = useNavigationHistory();
  const { user } = useAuth();
  const { getLandingPage } = useUserPersona();
  // Shared pages (settings, support) must not send a buyer to seller inventory.
  const resolvedFallback =
    fallbackPath === LANDING_FALLBACK ? (user ? getLandingPage() : '/') : fallbackPath;

  const handleClick = () => {
    if (onBeforeNavigate) {
      const result = onBeforeNavigate();
      if (result === false) return;
    }
    goBack(resolvedFallback);
  };

  if (iconOnly) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        className={cn('shrink-0', className)}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors',
        className
      )}
    >
      <ArrowLeft className="w-4 h-4" />
      {fallbackLabel}
    </button>
  );
};

export default BackButton;
